import { Box, IconButton, Link, Typography, useTheme } from '@mui/material';

import IconEdit from '@assets/icons/edit.svg?react';
import { MutableRefObject, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { titleStyles } from './infoStyles';
import { CustomGridApi } from '../XTable/types';
import FieldSearch from '../FieldSearch';
import useInfoList from './hooks/useInfoList';
import { GroupedField } from './types';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePage } from '@/contexts/usePageContext';
import { isWeightModelField } from '@/utils/structureUtils';

type InfoListProps = {
 data: unknown;
 customFields?: string[] | null;
 apiRef?: MutableRefObject<CustomGridApi>;
};

const InfoList = ({ data, apiRef, customFields }: InfoListProps) => {
 const th = useTheme();
 const { t } = useTranslation();
 const {
  filteredGroupKeys,
  filteredGroups,
  getMatchRange,
  groupKeys,
  normalizedSearch,
  searchValue,
  setSearchValue,
 } = useInfoList({
  customFields,
  data,
 });
 const { pageName } = usePage();
 const { hasRole } = useAuthContext();
 const handleEditClick = () => {
  const itemId = (data as { Id?: number } | null | undefined)?.Id;
  if (!apiRef?.current || !itemId) {
   return;
  }
  apiRef.current.setModalState({
   id: itemId,
   action: 'update',
  });
 };

 const highlightMatch = (text: string): ReactNode => {
  const matchRange = getMatchRange(text);
  if (!matchRange) {
   return text;
  }
  const start = text.slice(0, matchRange.start);
  const match = text.slice(matchRange.start, matchRange.end);
  const end = text.slice(matchRange.end);
  return (
   <>
    {start}
    <Box
     component="span"
     sx={{ backgroundColor: th.palette.warning.light, borderRadius: '2px' }}
    >
     {match}
    </Box>
    {end}
   </>
  );
 };

 if (groupKeys.length === 0) {
  return (
   <Box
    sx={{
     textAlign: 'center',
     p: '12px',
     backgroundColor: th.palette.kemek.lightBlue,
    }}
   >
    <Typography variant="body2" color="text.secondary">
     {t('global.noItemsFound')}
    </Typography>
   </Box>
  );
 }

 return (
  <>
   <Box sx={{ mb: '20px' }}>
    <FieldSearch
     searchValue={searchValue}
     setSearchValue={setSearchValue}
     label={t('global.search')}
     bordered={true}
    />
   </Box>
   {filteredGroupKeys.length === 0 ? (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   ) : (
    filteredGroupKeys.map((group) => (
     <Box key={group} sx={{ '&:last-child': { mb: 0 }, mb: '20px' }}>
      <Typography
       sx={{
        ...titleStyles,
        mb: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'space-between',
       }}
      >
       {group === 'null' ? t('global.data') : group}
       {hasRole(pageName, 'Update') || hasRole(pageName, 'Patch') ? (
        <IconButton onClick={handleEditClick} disabled={!apiRef}>
         <IconEdit style={{ color: th.palette.kemek.darkestGray }} />
        </IconButton>
       ) : null}
      </Typography>

      <Box
       sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        border: `1px solid ${th.palette.kemek.lightestGray}`,
        borderRadius: 1,
        px: '12px',
        py: 1,
       }}
      >
       {filteredGroups[group]?.map((item: GroupedField) => {
        const hasSubArray =
         item.ForeignKeyUrl && item.ForeignKeyUrl.split('/').length > 4;
        const href =
         item.ForeignKeyUrl && item.Value !== '-'
          ? `${item.ForeignKeyUrl.split('/')[2]?.toLowerCase()}?id=${
             item.InnerValue
            }`
          : '';
        const showModelLink =
         item.Type === 'model' &&
         !isWeightModelField(item) &&
         item.Value !== null &&
         item.Value !== undefined &&
         item.Value !== '-' &&
         !hasSubArray;

        return (
         <Box key={item.Key}>
          <Box
           sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
           }}
          >
           <Typography
            component="span"
            sx={{
             fontWeight: 'medium',
             fontSize: 12,
            }}
           >
            {highlightMatch(item.Translation || '')}
           </Typography>
           {showModelLink ? (
            <Link
             href={href}
             target="_blank"
             sx={{ fontSize: 12, textAlign: 'right', wordBreak: 'break-word' }}
            >
             {normalizedSearch && item.RenderedValueText
              ? highlightMatch(item.RenderedValueText)
              : item.RenderedValue}
            </Link>
           ) : (
            <Typography
             sx={{ fontSize: 12, textAlign: 'right', wordBreak: 'break-word' }}
            >
             {normalizedSearch && item.RenderedValueText
              ? highlightMatch(item.RenderedValueText)
              : item.RenderedValue}
            </Typography>
           )}
          </Box>
         </Box>
        );
       })}
      </Box>
     </Box>
    ))
   )}
  </>
 );
};

export default InfoList;

